/*
 * Dashboard row rules, with no Angular in them.
 *
 * Two decisions live here rather than in the component, because both are the
 * kind that fail silently on screen and neither needs a view to be true:
 *
 *  1. **Whether a row can be opened at all.** An organization nobody has
 *     planned carries no run, and the review endpoint answers an error for
 *     it -- so navigating there renders the review screen's red "could not be
 *     loaded" panel, which tells an operator the screen is broken when the
 *     truth is only that no work has been done for that customer yet.
 *
 *  2. **How a freshly read dashboard folds into the rows already on screen.**
 *     MatTable diffs its data by object IDENTITY. Replacing the array with
 *     new objects therefore destroys and re-creates every row view even when
 *     nothing changed, which on a background refresh reads as the whole
 *     screen reloading underneath whatever the operator was doing. Merging in
 *     place means the differ sees no change and only the handful of bindings
 *     that actually moved are updated.
 *
 * Both are covered by tests/ProjectMappingDashboardRows.smoke.ts. Keep this
 * file free of @angular imports: the smoke harness runs under ts-node in
 * CommonJS and cannot require() Angular 16's ESM.
 */
import {ProjectMappingDashboardRow} from '../../../../model/ProjectMapping';

/**
 * True when there is a plan to look at.
 *
 * `NOT_PLANNED` is checked as well as `run`, not instead of it: the two are
 * meant to agree, and when they do not the state is the more conservative
 * answer -- a run id left on a row whose state says nothing has been planned
 * is not something to navigate on.
 */
export function hasPlan(row: ProjectMappingDashboardRow): boolean {
  return !!row && !!row.run && !!row.run.runId && row.state !== 'NOT_PLANNED';
}

/**
 * True when the review screen will have proposals to show for this row.
 *
 * This decides WORDING, not whether the row can be clicked. Every row opens;
 * refusing the click was tried on 2026-10-24 and was worse, because the
 * dashboard payload currently reports `run: null` and `NOT_PLANNED` for
 * every organization -- the server builds that row from live Book and Draft
 * counts and does not read the run table yet -- so "only open rows that have
 * a plan" meant no row ever opened at all.
 *
 * A run still in `PLANNING` has nothing on the other side yet, so it is
 * excluded for the same reason as `NOT_PLANNED`. `APPLYING` is deliberately
 * NOT excluded: that plan exists, and watching it being applied is a
 * reasonable thing to want.
 */
export function hasReadyPlan(row: ProjectMappingDashboardRow): boolean {
  return hasPlan(row) && !!row.run && row.run.phase !== 'PLANNING';
}

/**
 * Fold a freshly read dashboard into the rows already on screen, keeping the
 * SAME row object for every organization that is still present.
 *
 * Row order follows the server's: new organizations appear where the server
 * puts them, and ones no longer in scope drop out by not being copied across.
 *
 * `adminEmailById` is re-applied on the way through because the dashboard
 * payload carries no email at all -- it is joined client-side -- so an
 * incoming row would otherwise overwrite the joined address with nothing,
 * blanking the email line and the email search along with it.
 */
export function mergeDashboardRows(existingRows: ProjectMappingDashboardRow[],
                                   incoming: ProjectMappingDashboardRow[],
                                   adminEmailById: { [id: number]: string }): ProjectMappingDashboardRow[] {
  const existingById: { [id: number]: ProjectMappingDashboardRow } = {};
  (existingRows || []).forEach(row => existingById[row.org_ID] = row);

  const merged: ProjectMappingDashboardRow[] = [];
  (incoming || []).forEach(fresh => {
    const existing = existingById[fresh.org_ID];
    const row = existing || fresh;
    if ( existing ) {
      existing.name = fresh.name;
      existing.serviceProvider_ID = fresh.serviceProvider_ID;
      existing.state = fresh.state;
      existing.unmappedBooks = fresh.unmappedBooks;
      existing.unmappedDrafts = fresh.unmappedDrafts;
      existing.totalBooks = fresh.totalBooks;
      existing.totalDrafts = fresh.totalDrafts;
      existing.run = fresh.run;
      existing.proposals = fresh.proposals;
    }
    if ( adminEmailById && adminEmailById[row.org_ID] ) {
      row.adminEmail = adminEmailById[row.org_ID];
    }
    merged.push(row);
  });
  return merged;
}
